/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ErrorType, ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import {deleteAttachment, findAttachments, getAttachments, uploadAttachment} from '../util/requestSender';

import {deepClone} from '../util/utils';

interface Props extends BaseProps {
    account_id: string;
    name: string;
    size: number;
}

interface Fields extends BaseFields {
    account_id: string;
    name: string;
    size: number;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    account_id: '',
    name: '',
    size: 0
};

const fieldsDefinition: {[key in keyof Fields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    name: createFieldDef(ModelFieldType.STRING),
    size: createFieldDef(ModelFieldType.NUMBER)
};

const ignoreProps: Array<keyof Props> = [];

type UploadCallback = (attachments: Attachment[], error: ErrorType) => void;
export default class Attachment extends BaseModel<Props, Fields> {
    static getAll = (
        account_id: string,
        limit: number = 10,
        page: number = -1,
        callback: ModelResponse<Attachment[]>,
        searchValue?: string
    ) => {
        getAttachments({
            data: {limit, page, account_id, search_value: searchValue},
            callback: (data: ResponseData) => {
                const {success} = data,
                    attachments: Attachment[] = success ? data.attachments.map((inv: any) => new Attachment(inv)) : [];
                callback(attachments, success, data);
            }
        });
    };

    static findAll = (attachmentIds: string[], accountId: string, callback: ModelResponse<Attachment[]>) => {
        findAttachments({
            data: {account_id: accountId, attachment_ids: attachmentIds},
            callback: (data: ResponseData) => {
                const attachments: Attachment[] = data.success
                    ? data.attachments.map((att: any) => new Attachment(att))
                    : [];
                callback(attachments, data.success, data);
            }
        });
    };

    static upload = (files: FileList, accountId: string, callback: UploadCallback) => {
        recursiveUpload(Array.from(files), accountId, callback);
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get account_id(): string {
        return this.fields.account_id;
    }

    get name(): string {
        return this.fields.name;
    }

    set name(name: string) {
        this.fields.name = name;
    }

    get size(): number {
        return this.fields.size;
    }

    set size(size: number) {
        this.fields.size = size;
    }

    clone = (deep: boolean = true): Attachment => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new Attachment(fields);
    };

    delete = (callback: ModelResponse<Attachment>) => {
        deleteAttachment({
            data: {account_id: this.account_id, attachment_id: this.id},
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}

const recursiveUpload = (
    files: File[],
    accountId: string,
    callback: UploadCallback,
    attachments: Attachment[] = [],
    error: ErrorType = null
) => {
    if (!files.length) return callback(attachments, error);
    const file = files.pop();
    if (!file) return callback(attachments, error);
    uploadAttachment({
        file: file,
        account_id: accountId,
        callback: (data: ResponseData) => {
            if (data.success) {
                attachments.push(new Attachment(data.attachment));
                recursiveUpload(files, accountId, callback, attachments, error);
            } else {
                error = data.message ?? `Het uploaden van bijlage ${file.name} is niet gelukt.`;
                callback(attachments, error);
            }
        }
    });
};
